import { useMemo } from 'react';
import { selectAdminLanguage, useAdminLanguageStore, type AppLanguageCode } from '@/stores/language.store';
import en from './locales/en.json';
import pl from './locales/pl.json';
import uk from './locales/uk.json';

const translations = {
  en,
  pl,
  uk,
} as const;

type TranslationKey = keyof typeof en;

export function useI18n() {
  const language = useAdminLanguageStore(selectAdminLanguage);

  const t = useMemo(() => {
    return (key: string, variables?: Record<string, string | number>): string => {
      const langTranslations = (translations[language] || translations.en) as Record<string, string>;
      const enTranslations = translations.en as Record<string, string>;
      let val = langTranslations?.[key] ?? enTranslations?.[key] ?? key;
      if (variables) {
        Object.entries(variables).forEach(([k, v]) => {
          val = val.replace(new RegExp(`{{\\s*${k}\\s*}}`, 'g'), String(v));
        });
      }
      return val;
    };
  }, [language]);

  return {
    language,
    setLanguage: useAdminLanguageStore((state) => state.setLanguage),
    t,
  };
}

export function resolveDocumentLanguage(language: AppLanguageCode): string {
  if (language === 'pl') return 'pl';
  if (language === 'uk') return 'uk';
  return 'en';
}

/** Locale dùng cho Intl/toLocaleDateString. `englishLocale` cho phép giữ nguyên biến thể en-US / en-GB sẵn có. */
export function resolveDateLocale(language: AppLanguageCode, englishLocale = 'en-US'): string {
  if (language === 'pl') return 'pl-PL';
  if (language === 'uk') return 'uk-UA';
  return englishLocale;
}
